import { branchesService } from "@/services/branchesService";
import { legalEntitiesService } from "@/services/legalEntitiesService";
import type { EntityAddressFormValues } from "./EntityAddressFields";

export function pickAddressValues(
  source: Partial<EntityAddressFormValues>,
): EntityAddressFormValues {
  return {
    addressSubDwelling: source.addressSubDwelling ?? "",
    addressNameNumber: source.addressNameNumber ?? "",
    street: source.street ?? "",
    locality: source.locality ?? "",
    town: source.town ?? "",
    county: source.county ?? "",
    postcode: source.postcode ?? "",
    uprn: source.uprn ?? "",
    countryCode: source.countryCode ?? "",
  };
}

function trimOrNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed || null;
}

/** Numeric or numeric+suffix (e.g. 10, 12A) → building number; otherwise building name. */
export function splitBuildingNameNumber(value: string): {
  buildingName: string | null;
  buildingNumber: string | null;
} {
  const trimmed = value.trim();
  if (!trimmed) return { buildingName: null, buildingNumber: null };
  if (/^\d+[A-Za-z]?$/.test(trimmed)) {
    return { buildingName: null, buildingNumber: trimmed };
  }
  return { buildingName: trimmed, buildingNumber: null };
}

export function joinBuildingNameNumber(
  buildingName?: string | null,
  buildingNumber?: string | null,
): string {
  return (buildingNumber ?? "").trim() || (buildingName ?? "").trim();
}

function buildAddressFull(
  subDwelling: string | null,
  buildingNumber: string | null,
  buildingName: string | null,
  street: string | null,
  town: string | null,
  postcode: string | null,
): string | null {
  const line1 = [subDwelling, buildingNumber, buildingName].filter(Boolean).join(" ").trim();
  const parts = [line1 || null, street, town, postcode].filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : null;
}

export function toLegalEntityRegisteredAddressPayload(values: EntityAddressFormValues) {
  const { buildingName, buildingNumber } = splitBuildingNameNumber(values.addressNameNumber);
  const registeredAddressSubDwelling = trimOrNull(values.addressSubDwelling);
  const registeredStreet = trimOrNull(values.street);
  const registeredLocality = trimOrNull(values.locality);
  const registeredTown = trimOrNull(values.town);
  const registeredCounty = trimOrNull(values.county);
  const registeredPostcode = trimOrNull(values.postcode);
  const registeredCountryCode = trimOrNull(values.countryCode);
  const registeredUprn = trimOrNull(values.uprn);

  return {
    registeredAddressFull: buildAddressFull(
      registeredAddressSubDwelling,
      buildingNumber,
      buildingName,
      registeredStreet,
      registeredTown,
      registeredPostcode,
    ),
    registeredAddressSubDwelling,
    registeredBuildingName: buildingName,
    registeredBuildingNumber: buildingNumber,
    registeredStreet,
    registeredLocality,
    registeredTown,
    registeredCounty,
    registeredPostcode,
    registeredCountryCode,
    registeredUprn,
  };
}

export function toBranchAddressPayload(values: EntityAddressFormValues) {
  const { buildingName, buildingNumber } = splitBuildingNameNumber(values.addressNameNumber);
  return {
    addressSubDwelling: trimOrNull(values.addressSubDwelling),
    buildingName,
    buildingNumber,
    street: trimOrNull(values.street),
    locality: trimOrNull(values.locality),
    town: trimOrNull(values.town),
    county: trimOrNull(values.county),
    postcode: trimOrNull(values.postcode),
    countryCode: trimOrNull(values.countryCode),
    uprn: trimOrNull(values.uprn),
  };
}

export function legalEntityAddressFromApi(source: {
  registeredAddressSubDwelling?: string | null;
  registeredBuildingName?: string | null;
  registeredBuildingNumber?: string | null;
  registeredStreet?: string | null;
  registeredLocality?: string | null;
  registeredTown?: string | null;
  registeredCounty?: string | null;
  registeredPostcode?: string | null;
  registeredCountryCode?: string | null;
  registeredUprn?: string | null;
}): EntityAddressFormValues {
  return {
    addressSubDwelling: source.registeredAddressSubDwelling ?? "",
    addressNameNumber: joinBuildingNameNumber(
      source.registeredBuildingName,
      source.registeredBuildingNumber,
    ),
    street: source.registeredStreet ?? "",
    locality: source.registeredLocality ?? "",
    town: source.registeredTown ?? "",
    county: source.registeredCounty ?? "",
    postcode: source.registeredPostcode ?? "",
    uprn: source.registeredUprn ?? "",
    countryCode: source.registeredCountryCode ?? "",
  };
}

export function branchAddressFromApi(source: {
  addressSubDwelling?: string | null;
  buildingName?: string | null;
  buildingNumber?: string | null;
  street?: string | null;
  locality?: string | null;
  town?: string | null;
  county?: string | null;
  postcode?: string | null;
  countryCode?: string | null;
  uprn?: string | null;
}): EntityAddressFormValues {
  return {
    addressSubDwelling: source.addressSubDwelling ?? "",
    addressNameNumber: joinBuildingNameNumber(source.buildingName, source.buildingNumber),
    street: source.street ?? "",
    locality: source.locality ?? "",
    town: source.town ?? "",
    county: source.county ?? "",
    postcode: source.postcode ?? "",
    uprn: source.uprn ?? "",
    countryCode: source.countryCode ?? "",
  };
}

/** Drop null values — PATCH treats null as untouched, so only send keys with values. */
export function omitNullValues<T extends Record<string, string | null>>(
  obj: T,
): Partial<T> {
  return Object.fromEntries(
    Object.entries(obj).filter(([, value]) => value != null),
  ) as Partial<T>;
}

export function extractCreatedEntityId(
  created: unknown,
): string {
  const record = created as
    | { id?: string | null; legalEntityId?: string | null; branchId?: string | null; entityId?: string | null }
    | null
    | undefined;
  return (
    record?.id?.trim() ||
    record?.legalEntityId?.trim() ||
    record?.branchId?.trim() ||
    record?.entityId?.trim() ||
    ""
  );
}

/** Persist registered address on the legal entity row (hierarchy PATCH does not write these). */
export async function persistLegalEntityAddress(
  id: string,
  patch: Partial<ReturnType<typeof toLegalEntityRegisteredAddressPayload>>,
  options?: { includeNulls?: boolean },
): Promise<void> {
  const toSave = options?.includeNulls
    ? patch
    : omitNullValues(patch as Record<string, string | null>);
  if (Object.keys(toSave).length === 0) return;
  await legalEntitiesService.update(id, toSave);
}

/** Persist branch address on the branch row (hierarchy PATCH does not write these). */
export async function persistBranchAddress(
  id: string,
  patch: Partial<ReturnType<typeof toBranchAddressPayload>>,
  options?: { includeNulls?: boolean },
): Promise<void> {
  const toSave = options?.includeNulls
    ? patch
    : omitNullValues(patch as Record<string, string | null>);
  if (Object.keys(toSave).length === 0) return;
  await branchesService.update(id, toSave);
}

/** Include only keys whose values changed (for PATCH — omitted keys are left alone). */
export function diffRecord<T extends Record<string, string | null>>(
  next: T,
  initial: T,
): Partial<T> {
  const updates: Partial<T> = {};
  for (const key of Object.keys(next) as (keyof T)[]) {
    if (next[key] !== initial[key]) {
      updates[key] = next[key];
    }
  }
  return updates;
}
